import React from 'react';
import { useAtomValue } from 'jotai';
import { hasPermissionAtom } from '@/store';

interface PermissionGuardProps {
  permission: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * PermissionGuard component - conditionally renders children based on user permissions
 *
 * @param permission - The permission string to check (e.g., "User.index", "Domain.store")
 * @param children - Content to render if user has permission
 * @param fallback - Optional content to render if user doesn't have permission
 *
 * @example
 * <PermissionGuard permission="User.index">
 *   <Button>Manage Users</Button>
 * </PermissionGuard>
 *
 * <PermissionGuard permission="Domain.store" fallback={<div>Access Denied</div>}>
 *   <AddDomainButton />
 * </PermissionGuard>
 */
export function PermissionGuard({ permission, children, fallback = null }: PermissionGuardProps) {
  const hasPermission = useAtomValue(hasPermissionAtom);

  if (hasPermission(permission)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

/**
 * Hook for checking permissions in components
 *
 * @example
 * const hasPermission = useHasPermission();
 *
 * if (hasPermission("User.index")) {
 *   // Show user management features
 * }
 */
export function useHasPermission() {
  const hasPermission = useAtomValue(hasPermissionAtom);
  return hasPermission;
}
